import { Box, Divider, Popper, Stack, styled, SxProps, TextField, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { ConnectionPointDto, useGetPlanConnectionPoints, useGetTeams } from "../../http";
import { ITeamSummary } from "../../models";
import { Autocomplete } from "../Autocomplete";
import { isWhitespace } from "../textEditor";
import { useSession } from "../../hooks";
import { useParams } from "react-router";

const MenuItem = styled(Box)`
  &:first-child {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }

  &:last-child {
    margin-bottom: 2px;
  }
` as typeof Box;

interface UrlParams {
  slug: string;
}

interface IProps {
  actionItemGuid?: string;
  disabled?: boolean;
  showLabels?: boolean;
  teamOptionDisabled?: (team: ITeamSummary) => boolean;
  defaultContext?: {
    teamSlug: string;
    actionItemGuid: string;
  };
  shouldHideChildren?: boolean;
  shouldHideParents?: boolean;
  onSelect: (connectionPoint: ConnectionPointDto | null) => void;
  sx?: SxProps;
}

const StrategicContextPicker = (props: IProps) => {
  const { slug } = useParams<keyof UrlParams>() as UrlParams;
  const { userId } = useSession();
  const [selectedTeam, setSelectedTeam] = useState<ITeamSummary | null>(null);
  const [selectedConnectionPoint, setSelectedConnectionPoint] = useState<ConnectionPointDto | null>(null);
  const { data: teams, isLoading: isLoadingTeams } = useGetTeams(userId);
  const { data: connectionPoints, isFetching: isFetchingConnectionPoints } = useGetPlanConnectionPoints(
    selectedTeam?.slug,
    props.actionItemGuid
  );
  const theme = useTheme();

  useEffect(() => {
    if (props.defaultContext) {
      setSelectedTeam(teams?.find((x) => x.slug === props.defaultContext?.teamSlug && x.planGuid !== null) ?? null);
    } else {
      setSelectedTeam(teams?.find((x) => x.slug === slug && x.planGuid !== null) ?? null);
    }
  }, [props.defaultContext, slug, teams]);

  useEffect(() => {
    if (props.defaultContext) {
      const connectionPoint =
        connectionPoints?.find((x) => x.actionItemGuid === props.defaultContext?.actionItemGuid) ?? null;
      setSelectedConnectionPoint(connectionPoint);
      props.onSelect(connectionPoint);
    } else {
      setSelectedConnectionPoint(null);
      props.onSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultContext, connectionPoints]);

  function handleTeamChanged(team: ITeamSummary | null) {
    setSelectedTeam(team);
    setSelectedConnectionPoint(null);
  }

  function handleConnectionPointChanged(connectionPoint: ConnectionPointDto | null) {
    setSelectedConnectionPoint(connectionPoint);
    props.onSelect(connectionPoint);
  }

  function sortFunction(a: ITeamSummary, b: ITeamSummary) {
    let aMembership = a.isAdministrator || a.isMember;
    let bMembership = b.isAdministrator || b.isMember;

    if (aMembership !== bMembership) {
      return aMembership ? -1 : 1;
    }

    return a.name.localeCompare(b.name);
  }

  return (
    <Stack spacing={props.showLabels ? 1 : 0.5} sx={props.sx}>
      <Stack spacing={0.25}>
        {props.showLabels && <Typography variant="label">Team</Typography>}
        <Autocomplete
          isLoading={isLoadingTeams}
          disabled={isLoadingTeams || props.disabled}
          groupBy={(option) => (option.isMember || option.isAdministrator ? "My Teams" : "Other Teams")}
          options={teams ? [...teams].sort(sortFunction) : []}
          getOptionLabel={(option) => option.name}
          renderGroup={(group) => (
            <Box key={group.key} sx={{ pb: 0.25 }}>
              <Stack
                spacing={0.2}
                sx={{ px: 0.5, pt: 0.75, position: "sticky", top: 0, backgroundColor: theme.palette.common.white }}
              >
                <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                <Divider />
              </Stack>
              {group.children}
            </Box>
          )}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.slug} component="li">
              <Stack>
                <Typography>{option.name}</Typography>
                {option.planGuid === null && <Typography variant="caption">This team has no strategic map</Typography>}
              </Stack>
            </MenuItem>
          )}
          getOptionDisabled={(option) => option.planGuid === null || props.teamOptionDisabled?.(option) === true}
          isOptionEqualToValue={(option, value) => option.slug === value.slug}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select a team..." inputProps={{ ...params.inputProps }} />
          )}
          onChange={(_, value) => handleTeamChanged(value)}
          PopperComponent={(props) => (
            <Popper {...props} style={{ ...props.style, margin: 0, padding: 0, minWidth: "300px" }} />
          )}
          value={selectedTeam}
        />
      </Stack>

      <Stack spacing={0.25}>
        {props.showLabels && <Typography variant="label">Strategic Item</Typography>}
        <Autocomplete
          isLoading={isFetchingConnectionPoints}
          disabled={!selectedTeam || !connectionPoints || props.disabled}
          groupBy={(option) => option.sectionName!}
          options={
            connectionPoints?.filter(
              (x) =>
                x.actionItemGuid !== props.actionItemGuid &&
                x.description &&
                !isWhitespace(x.description) &&
                !(props.shouldHideChildren && x.isChild) &&
                !(props.shouldHideParents && x.isParent)
            ) ?? []
          }
          getOptionLabel={(option) => option.description!}
          renderGroup={(group) => (
            <Box key={group.key} sx={{ pb: 0.25 }}>
              <Stack
                spacing={0.2}
                sx={{ px: 0.5, pt: 0.75, position: "sticky", top: 0, backgroundColor: theme.palette.common.white }}
              >
                <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                <Divider />
              </Stack>
              {group.children}
            </Box>
          )}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.actionItemGuid} component="li">
              <Typography>{option.description}</Typography>
            </MenuItem>
          )}
          isOptionEqualToValue={(option, value) => option.actionItemGuid === value.actionItemGuid}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select a strategic map item..." inputProps={{ ...params.inputProps }} />
          )}
          onChange={(_, value) => handleConnectionPointChanged(value)}
          PopperComponent={(props) => (
            <Popper {...props} style={{ ...props.style, margin: 0, padding: 0, minWidth: "300px" }} />
          )}
          value={selectedConnectionPoint}
        />
      </Stack>
    </Stack>
  );
};

export { StrategicContextPicker };
